/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  OutlinedInput,
  Popover,
  Stack,
  styled,
  SvgIconTypeMap,
  Typography
} from '@mui/material';
import { FC, useState } from 'react';
import { TSocialMedia } from 'src/models/general';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedinIcon from '@mui/icons-material/LinkedIn';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IObjectInput, TObjectType } from 'src/models/objectManagement';
import { useAlert } from 'src/hooks/useAlert';
import GetSocialMediaIcon from 'src/components/GetSocialMediaIcon';
import TiktokIcon from 'src/components/TikTokIcon/TikTokIcon';
import { isIncludeUrlComponent } from 'src/utils/isIncludeUrlComponent';
import { isValidURL } from 'src/utils/isValidURL';
import { getSocmedDomain } from 'src/utils/getSocmedDomain';
import ThreadsIcon from 'src/components/Threads/ThreadsIcon';
import { reducerUpdateSelectedTempSocmed } from 'src/features/objectManagement';
import { t } from 'i18next';

interface ISocialMedia {
  id: TSocialMedia;
  label: string;
  icon:
    | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
        muiName: string;
      })
    | any;
}

interface Props {
  objectType?: TObjectType;
  onAdd: (tempObjectInput: IObjectInput) => void;
}

const CustomButton = styled(Button)(
  ({ theme }) => `
    border: unset;
    border-radius: 6px;
    background-color: white;
    border: 1px solid ${theme.palette.divider};
    border-color: ${theme.palette.common.black}
    color: ${theme.palette.primary.main};
    &:hover{
      background-color: ${theme.palette.grey[300]};
    }
    margin-right: 4px;
  `
);

const ListItemButtonCustom = styled(ListItemButton)(
  ({ theme }) => `
    &:hover {
      background-color: ${theme.palette.primary.light}
    }
  `
);

const listOptionSocialMedia: Array<ISocialMedia> = [
  {
    id: 'twitter',
    label: 'X',
    icon: TwitterIcon
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: FacebookIcon
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: InstagramIcon
  },
  {
    id: 'youtube',
    label: 'Youtube',
    icon: YouTubeIcon
  },
  { id: 'tiktok', label: 'Tiktok', icon: TiktokIcon },
  {
    id: 'threads',
    label: 'Threads',
    icon: ThreadsIcon
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: LinkedinIcon
  }
];

const InputSocialProfile: FC<Props> = ({ objectType = 'account', onAdd }) => {
  const dispatch = useAppDispatch();

  const { tempInputObjects } = useAppSelector(
    (state) => state.objectManagement
  );
  const { basicObjectQuota, basicObjectQuotaUsed } = useAppSelector(
    (state) => state.users.project.onBoarding
  );
  const [socialMedia, setSocialMedia] = useState<TSocialMedia>('twitter');
  const [content, setContent] = useState<string>('');

  const { handleClickAlert } = useAlert();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleChangeSocialMedia = (socialMediaId: TSocialMedia) => {
    setSocialMedia(socialMediaId);
    handleClose();
  };

  const handleAdd = () => {
    if (basicObjectQuotaUsed + tempInputObjects.length > basicObjectQuota) {
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        message:
          'Your social profile quota is full. Remove an existing social profile to add a new one.',
        severity: 'error'
      });
      return;
    }
    const formattedContent =
      isIncludeUrlComponent(content) || isValidURL(content)
        ? content
        : getSocmedDomain(socialMedia) + content;

    // jika udah ada di selected
    const isAvailableInMain = tempInputObjects.find(
      (tempObj) => tempObj.content === formattedContent
    );

    if (isAvailableInMain) {
      handleClickAlert({
        horizontal: 'center',
        vertical: 'top',
        severity: 'error',
        message: `Social profile "${content}" has already selected`
      });
      return;
    }

    const tempObjectInput: IObjectInput = {
      content: formattedContent,
      displayPic: '',
      excludes: [],
      extraContent: '',
      includes: [],
      isHashtag: false,
      languages: '',
      objectName: formattedContent,
      socialMedia: socialMedia,
      streamType: objectType
    };

    onAdd(tempObjectInput);
    handleClear();
  };

  const handleClear = () => {
    dispatch(reducerUpdateSelectedTempSocmed(null));
    setContent('');
  };

  const disableSave =
    !content ||
    Boolean(
      tempInputObjects.find(
        (item) => item.content === getSocmedDomain(socialMedia) + content
      )
    );

  const handleChangeContent = (event: React.ChangeEvent<HTMLInputElement>) => {
    setContent(event.target.value);
    if (isIncludeUrlComponent(event.target.value)) {
      const socialMediaFromUrl = listOptionSocialMedia.find((option) =>
        event.target.value.includes(getSocmedDomain(option.id))
      );
      if (socialMediaFromUrl) {
        setSocialMedia(socialMediaFromUrl.id);
      }
    }
  };

  return (
    <Stack spacing={1}>
      <Typography fontWeight={700} fontSize="13px">
        {t('enter social media')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        <CustomButton
          variant="contained"
          onClick={handleClick}
          disabled={isIncludeUrlComponent(content)}
        >
          <GetSocialMediaIcon socialMedia={socialMedia} color="primary" />
          <ArrowDropDownIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
        </CustomButton>
        <OutlinedInput
          fullWidth
          placeholder={
            socialMedia === 'youtube' || socialMedia === 'linkedin'
              ? t('enter your social profile url here')
              : t('enter a username (without @) or url')
          }
          onChange={handleChangeContent}
          value={content}
          sx={{
            borderRadius: '6px'
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          my: 2
        }}
      >
        <Button variant="outlined" onClick={handleClear}>
          {t('clear')}
        </Button>
        <Button variant="contained" disabled={disableSave} onClick={handleAdd}>
          {t('add profile')}
        </Button>
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List>
          {listOptionSocialMedia.map((socialMedia) => (
            <ListItem disablePadding key={socialMedia.id}>
              <ListItemButtonCustom
                onClick={() => handleChangeSocialMedia(socialMedia.id)}
              >
                <ListItemIcon>{<socialMedia.icon />}</ListItemIcon>
                <ListItemText primary={socialMedia.label} />
              </ListItemButtonCustom>
            </ListItem>
          ))}
        </List>
      </Popover>
    </Stack>
  );
};

export default InputSocialProfile;
